<ion-toolbar>
  <ion-buttons slot="start">
    <ion-button fill="outline" type="button" color="secondary" (click)="back()">
      <ion-icon name="arrow-back"></ion-icon>
      <span class="hidden-md">&nbsp; {{ 'ACTION.back' | translate }}</span>
    </ion-button>
  </ion-buttons>
  <ion-title role="heading" aria-level="2">{{ 'TITLE.jobPerform' | translate }}</ion-title>
</ion-toolbar>

<ion-content>

  <section class="card ride">
    <h3 class="title">{{ jobEvent.headerInfo.type | translate }} - {{ jobEvent.headerInfo.details }}</h3>

    @if (jobEvent.location) {
      <app-address
        [location]="jobEvent.location"
        [phoneNumber]="jobEvent.phoneNumber" />
    }

    <dl>
      <dt>{{ 'LABEL.account' | translate }}:</dt>
      <dd>{{ job.accountCode }}</dd>

      <dt>{{ translationKeys.authNumber | translate }}:</dt>
      <dd>{{ job.authNumber }}</dd>

      @if (features.showClientId) {
        <div>
          <dt>{{ 'LABEL.riderId' | translate }} #:</dt>
          <dd>{{ job.riderId }}</dd>
        </div>
      }

      @if (job.rideComments) {
        <div>
          <dt>{{ 'LABEL.reservationSpecialDir' | translate }}:</dt>
          <dd>{{ job.rideComments }}</dd>
        </div>
      }

      @if (job.riderComments) {
        <div>
          <dt>{{ 'LABEL.riderSpecialDir' | translate }}:</dt>
          <dd>{{ job.riderComments }}</dd>
        </div>
      }
    </dl>
  </section>

  <!-- Signature -->
  @if (!isNoShow && job.riderSignatureRequired && !form.value.signature) {
    <section
      class="card signature"
      >
      <div class="header">
        <h3 class="title">{{ 'LABEL.signature' | translate }}</h3>
        <ion-button fill="outline" (click)="captureSignature()">
          {{ (this.perform.signature ? 'LABEL.retakeSignature' : 'LABEL.captureSignature') | translate }}
        </ion-button>
      </div>
      <div class="content">
        @if (job.riderSignatureRequired && !form.value.signature && !form.value.signatureReason) {
          <p>
            {{ 'LABEL.sigPerformDropOff' | translate }}
          </p>
        }
        @if (form.value.signature) {
          <img class="signature-image" [src]="form.value.signature"
            [alt]="'LABEL.signature' | translate">
        }
      </div>
    </section>
  }

  @if (addToBalanceEnabled && job.jobType === jobTypes.Pickup) {
    <section class="credit-card">
      <ion-button
        fill="outline"
        color="primary"
        expand="block"
        (click)="payWithCreditCard()"
        [disabled]="disconnected || handlingCCPayment">
        <div class="add-to-balance-button">
          <div class="add-to-balance-button-details">
            <ion-icon name="card-outline"></ion-icon>
            {{ 'CREDIT_CARDS.addToBalance' | translate }}
          </div>
          @if (disconnected) {
            <div class="offline">
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 18 18">
                <path fill-opacity=".3" d="M12 5h4V1zm0 11V5L1 16z" />
                <path d="M14 16h2v-2h-2v2zm0-9v5h2V7h-2z" />
              </svg>
              {{ 'LABEL.youAreOffline'| translate }}
              {{ 'CREDIT_CARDS.offline' | translate }}
            </div>
          }
        </div>
      </ion-button>
    </section>
  }

  <!-- Credit Card Payment -->
  @if (ridePaymentInfo?.hasPayment) {
    <section
      class="card payment" [ngClass]="ridePaymentInfo.isSuccessful ? 'success' : 'failed'">
      <p>
        <ion-icon
          [name]="ridePaymentInfo.isSuccessful ? 'check-circle-outline' : 'alert-circle-outline'"
          [color]="ridePaymentInfo.isSuccessful ? 'success' : 'danger'" />
        {{ 'LABEL.'+ridePaymentInfo.type | translate }}
        {{ (ridePaymentInfo.isSuccessful ? 'LABEL.paymentCollected' : 'LABEL.paymentCollectedFailed') | translate }}
      </p>
    </section>
  }

  <!-- Passes -->
  @if (!isNoShow && isPickup && paraPassEnabled && canCollectFare) {
    <section
      class="card pass"
      >
      <div class="header">
        <h3>
          @switch (true) {
            @case (form.value.passResults?.passInformation != null) {
              {{ 'LABEL.riderFareAccepted' | translate }}
            }
            @case (form.value.passResults && !form.value.passResults?.passInformation) {
              {{ 'LABEL.passWillBeDebitedFor' | translate }}: {{ getCurrency(job.rideFare) }}
            }
            @default {
              {{ 'LABEL.pleaseCollect' | translate }}: {{ getCurrency(job.rideFare) }}
            }
          }
        </h3>
        <div class="actions">
          @if (!form.value.passResults) {
            <ion-button
              fill="outline"
              color="primary"
              (click)="handlePass()"
              >
              {{ 'ACTION.scanPass'| translate }}
            </ion-button>
          }
          @if (form.value.passResults) {
            <ion-button
              fill="outline"
              color="danger"
              (click)="removePass()"
              >
              {{'ACTION.removePass' | translate}}
            </ion-button>
          }
        </div>
      </div>
      @if (form.value.passResults?.passInformation; as passInformation) {
        <dl>
          @if (passInformation?.passType === PassTypeEnum.Cash) {
            <dt>{{ 'LABEL.rideCost' | translate }}:</dt>
            <dd>{{ getCurrency(passInformation.debitAmoutForRide) }}</dd>
            <div class="line"></div>
            <dt>{{ 'LABEL.passBalance' | translate }}:</dt>
            <dd [ngClass]="{ 'negative-balance': passInformation.remainingBalance < 0 }">{{ getCurrency(passInformation.remainingBalance) }}</dd>
            <div class="line"></div>
            <dt>{{ translationKeys.cashCollected | translate }}:</dt>
            <dd>{{ getCurrency(form.value.fareCollected) }}</dd>
            <div class="line"></div>
            <dt>{{ 'LABEL.remainBalanceAfterDebit' | translate }}:</dt>
            <dd [ngClass]="{ 'negative-balance': (passInformation.balanceRemainingAfterRideDebit + form.value.fareCollected) < 0 }">{{ getCurrency(passInformation.balanceRemainingAfterRideDebit + form.value.fareCollected) }}</dd>
          }
          @if (passInformation?.passType === PassTypeEnum.Flex) {
            <dt>{{ 'LABEL.passEffective' | translate }}:</dt>
            <dd>{{ passInformation.effectiveDate | date:'MM/dd/yyyy' }}</dd>
            <div class="line"></div>
            <dt>{{ 'LABEL.passExpiration' | translate }}:</dt>
            <dd>{{ passInformation.expirationDate | date:'MM/dd/yyyy' }}</dd>
            <div class="line"></div>
            <dt>{{ 'LABEL.currentRidesRemain' | translate }}:</dt>
            <dd [ngClass]="{'negative-balance': (passInformation.remainingRides ?? 0) < 0  }">{{ passInformation.remainingRides != null ? passInformation.remainingRides : 'LABEL.unlimited' | translate }}</dd>
            <div class="line"></div>
            <dt>{{ 'LABEL.passDebitedFor' | translate }}:</dt>
            <dd>{{ form.value.passResults.passInformation.debitRideCountForRide }}</dd>
            <div class="line"></div>
            <dt>{{ 'LABEL.remainRidesAfterDebit' | translate }}:</dt>
            <dd [ngClass]="{ 'negative-balance' : (passInformation.remainingRides != null ? passInformation.remainingRides - 1 : 1) < 0 }">
              {{ passInformation.remainingRides != null ? passInformation.remainingRides - 1 : 'LABEL.unlimited' | translate }}
            </dd>
          }
        </dl>
      }
    </section>
  }

  <!-- Form Inputs -->
  <form class="card inputs" [formGroup]="form" id="jobPerformForm">

    <label id="odometer" aria-describedby="odoError">{{ 'LABEL.odometer' | translate }}</label>
    <div
      incrementControl
      [maxValue]="999999"
      [minValue]="0"
      formControlName="odometer"
      [integerOnly]="true"
      aria-labelledby="odometer"
      [required]="true">
      <form-error name="odometer" id="odoError" />
    </div>

    @if (isPickup) {
      @if (form.value.noShowReason !== null) {
        <label id="noShowOptionsLabel" for="noShowOptions">{{ 'LABEL.noShow' | translate }}</label>
        <ion-select
          aria-labelledby="noShowOptionsLabel"
          id="noShowOptions"
          formControlName="noShowReason"
          interface="popover"
          [interfaceOptions]="{'cssClass': 'wider-popover'}">
          @for (option of noShowOptions; track option) {
            <ion-select-option [value]="option.id">
              {{ option.displayValue }}
            </ion-select-option>
          }
        </ion-select>
      }
      @if (form.value.noShowReason === null) {
        <h4>{{ 'LABEL.rider' | translate }}</h4>
        <ng-container>
          <span>{{ 'LABEL.fareOwed' | translate }}</span>
          <div class="ion-text-right">{{ (features.additionalRideFare ? rideFare : this.perform.rideFare) | currency }}</div>
        </ng-container>
        @if (features.fareShowType) {
          <label id="fareTypeOptionsLabel" for="fareTypeOptions">{{ 'LABEL.fareType' | translate }}</label>
          <ion-select
            aria-labelledby="fareTypeOptionsLabel"
            formControlName="fareTypeId"
            id="fareTypeOptions"
            interface="popover"
            [interfaceOptions]="{'cssClass': 'wider-popover'}">
            @for (option of data.fareTypes; track option) {
              @if (showFareOption(option)) {
                <ion-select-option [value]="option.id">
                  {{ option.displayValue }}
                </ion-select-option>
              }
            }
          </ion-select>
        }
        @if (features.fareShowType && isFareTypeExternal() && !!job.externalAccount.name) {
          <dl>
            <dt>{{ 'LABEL.cardInUse' | translate }}</dt>
            <dd>
              <span>
                {{ job.externalAccount.name }}
              </span>
            </dd>
          </dl>
        }
        @if (remainingBalance < infinity) {
          <dl>
            <dt>{{ 'LABEL.balanceRemaining' | translate }}</dt>
          <dd [ngClass]="{
            low: remainingBalance < balanceWarning,
            negative: remainingBalance < 0
          }">
              {{ remainingBalance | currency }}
              <br>
                @if (remainingBalance < 0) {
                  <span class="balance negative" role="alert">
                    <ion-icon name="alert-circle-outline" />
                    {{ 'MESSAGE.negativeBalance' | translate }}
                  </span>
                }
                @if (remainingBalance >= 0 && remainingBalance <= balanceWarning) {
                  <span class="balance low" role="alert">
                    <ion-icon name="warning-outline" />
                    {{ 'MESSAGE.lowBalance' | translate }}
                  </span>
                }
              </dd>
            </dl>
          }
          @if (
            (features.hasAdditionalPassengerCount && form.controls.additionalRideCount.value > 0)
            || features.hasEscorts
            || features.hasChildren
            || (features.fareAdditionalPassengers && form.controls.additionalRideCount.value > 0)
            ) {
            <h4>
              {{ 'LABEL.additionalPassengers' | translate }}
            </h4>
            @if (features.hasAdditionalPassengerCount) {
              <span>{{ 'LABEL.numOfAdditionalPassengers' | translate }}</span>
              <div class="ion-text-right">{{ form.controls.additionalRideCount.value }}</div>
            }
            @if(features.fareAdditionalPassengers || features.additionalRideFare){
              <span>{{ 'LABEL.fareOwed' | translate }}</span>
              <div class="ion-text-right">{{ (features.additionalRideFare ? additionalRideFare : this.perform.additionalRideFare) | currency }}</div>
            }

            @if (features.hasEscorts) {
              <label for="numOfEscorts">{{ 'LABEL.numOfEscorts' | translate }}</label>
              <div
                class="increment"
                incrementControl
                [integerOnly]="true"
                [maxValue]="100"
                [minValue]="0"
                formControlName="numberOfEscorts"
                id="numOfEscorts">
              </div>
            }
            @if (features.hasChildren) {
              <label for="numOfChildren">{{ 'LABEL.numOfChildren' | translate }}</label>
              <div
                class="increment"
                incrementControl
                [integerOnly]="true"
                [maxValue]="100"
                [minValue]="0"
                formControlName="numberOfChildren"
                id="numOfChildren">
              </div>
            }
            @if (features.fareAdditionalPassengers) {
              <label id="additionalFareTypeIdLabel" for="additionalFareTypeId">{{ 'LABEL.fareType' | translate }}</label>
              <ion-select
                aria-labelledby="additionalFareTypeIdLabel"
                formControlName="additionalFareTypeId"
                id="additionalFareTypeId"
                interface="popover"
                [interfaceOptions]="{'cssClass': 'wider-popover'}">
                @for (option of data.fareTypes; track option) {
                  @if (showFareOption(option)) {
                    <ion-select-option [value]="option.id">
                      {{ option.displayValue}}
                    </ion-select-option>
                  }
                }
              </ion-select>
            }
          }
          <hr class="fare-collection-border" />
          @if (canCollectFare) {
            <h4>{{ 'LABEL.fareCollection' | translate }}</h4>
            <ng-container>
              <span>{{ 'LABEL.collectFromRider' | translate }}</span>
              <div class="ion-text-right">{{ expectedFareCollected | currency }}</div>
            </ng-container>
            <ng-container>
              <label for="fareCollected">{{ translationKeys.cashCollected | translate }}</label>
              <div>
                <ion-item fill="outline">
                  <app-currency
                    formControlName="fareCollected"
                    [required]="true"
                    aria-describedby="fareCollectedError">
                    <currency-start>$</currency-start>
                  </app-currency>
                </ion-item>
                <form-error name="fareCollected" id="fareCollectedError" />
              </div>
            </ng-container>
            @if (this.calculateOverage() > 0) {
              <span class="balance low" role="alert">
                <ion-icon name="warning-outline" />
                {{ overpaymentEnabled ? ('MESSAGE.overpaymentToBalance' | translate) : ('MESSAGE.overpaymentWarning' | translate) }}
              </span>
            }
            @if (this.calculateOverage() < 0) {
              <span class="balance low" role="alert">
                <ion-icon name="warning-outline" />
                {{ 'MESSAGE.underpaymentWarning' | translate }}
              </span>
            }
          }
          @if (features.showUnits) {
            <label for="numOfUnits">{{ 'LABEL.units' | translate }}</label>
            <div
              class="increment"
              incrementControl
              [integerOnly]="true"
              [maxValue]="100"
              [minValue]="0"
              formControlName="units"
              id="numOfUnits">
            </div>
          }
        }
      }

    </form>

    @if (features.driverNotes && !isNoShow) {
      <section class="card driver-notes" [attr.aria-label]="'LABEL.driverNotes' | translate" [formGroup]="form">
        <label id="driverNotes" position="stacked">{{ 'LABEL.driverNotes' | translate }}</label>
        <ion-item fill="outline">
          <ion-textarea
            maxlength="10000"
            formControlName="driverNotes"
            aria-labelledby="driverNotes"
            aria-describedby="driverNotesErrors"
            rows="5"
            />
        </ion-item>
        <form-error name="driverNotes" id="driverNotesErrors" />
      </section>
    }

  </ion-content>

  <ion-toolbar class="bottom-actions">
    <ion-button
      type="submit"
      form="jobPerformForm"
      [disabled]="disableSubmit()"
      expand="block"
      color="primary"
      (click)="onSubmit()">
      {{ 'LABEL.confirm' | translate }}
      {{ this.form.controls.noShowReason.value !== null ? ('LABEL.noShowWithDash' | translate) : jobEvent.label }}
    </ion-button>

    <ion-button type="button" fill="outline" expand="block" (click)="back()">
      {{ 'ACTION.cancel' | translate }}
    </ion-button>
  </ion-toolbar>
